import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isBefore } from "date-fns";
import { useEffect } from "react";
import { useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { useConfig } from "@/providers/config/useConfig";
import { surveyMutations, useSurveyQueries } from "@/queries/surveys";

import type { LayoutProps } from "./Layout";

interface Props {
  children: (data: LayoutProps) => React.ReactNode;
}

export function Loader(props: Props): React.ReactNode {
  const { id: surveyId } = useParams<{ id: string }>();

  const surveyAiFollowUpActions = useConfig("surveyAiFollowUpSuggestions");
  const queryClient = useQueryClient();
  const surveyQueries = useSurveyQueries();
  const {
    data: survey,
    error: errorSurveyDetails,
    isPending: isPendingSurveyDetails,
  } = useQuery({
    ...surveyQueries.details({ surveyId: surveyId!, category: "other" }),
    enabled: !!surveyId,
  });
  const deleteSurvey = surveyMutations.useDeleteSurvey();

  const isFinished = !!survey?.endsAt && isBefore(survey.endsAt, new Date());

  useEffect(() => {
    if (isFinished && survey?.canViewResults && surveyAiFollowUpActions) {
      void queryClient.prefetchQuery(surveyQueries.suggestedFollowUps(survey.id));
    }
  }, [isFinished, survey?.canViewResults, survey?.id, queryClient, surveyQueries, surveyAiFollowUpActions]);

  if (isPendingSurveyDetails) {
    return <FullSizeLoader withPadding />;
  }

  if (errorSurveyDetails) {
    return <ErrorPage error={errorSurveyDetails} />;
  }

  return props.children({
    survey,
    isFinished,
    onDelete: deleteSurvey.mutateAsync,
  });
}
